import React, { useState, useEffect } from 'react';
import { Filter, RefreshCw, Search, X, Building2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { statsApi, aedApi } from '../utils/api';
import { AED_STATUSES, AED_STATUS_ORDER, STATUS_CONFIG } from '../utils/constants';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Slider } from './ui/slider';

// Derive status overview from a filtered list of AEDs (so overview matches grid when filters are on)
function deriveStatsFromAeds(aeds) {
  const status_counts = { READY: 0, NOT_READY: 0, REPOSITION: 0, AED_NOT_PRESENT: 0, BATTERY_EXPIRED: 0, EXPIRING_BATT_PADS: 0, CAMERA_ISSUE: 0, UNKNOWN: 0, LOST_CONTACT: 0 };
  let review_required = 0;
  let low_confidence_count = 0;
  for (const aed of aeds) {
    const s = aed.current_status || 'UNKNOWN';
    if (status_counts[s] !== undefined) status_counts[s]++;
    else status_counts.UNKNOWN++;
    if (aed.review_required) review_required++;
    if ((aed.ai_confidence ?? 0) < 0.85) low_confidence_count++;
  }
  const total = aeds.length;
  const reposition_count = status_counts.REPOSITION || 0;
  const reposition_rate = total > 0 ? (reposition_count / total * 100) : 0;
  return {
    total_aeds: total,
    status_counts,
    review_required,
    low_confidence_count,
    reposition_rate: Math.round(reposition_rate * 10) / 10,
  };
}

export const FilterPanel = () => {
  const { filters, updateFilter, clearFilters, triggerRefresh } = useApp();
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Overview only reflects subscriber (and review) filter — not status filter
  const fetchStats = async () => {
    setLoading(true);
    try {
      const overviewFilters = { subscriber: filters.subscriber ?? null, reviewRequired: filters.reviewRequired ?? null };
      const hasOverviewFilters = overviewFilters.subscriber != null || overviewFilters.reviewRequired != null;
      if (hasOverviewFilters) {
        const aeds = await aedApi.getAll(overviewFilters);
        setStats(deriveStatsFromAeds(aeds));
      } else {
        const data = await statsApi.get(overviewFilters);
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const data = await aedApi.getSubscribers();
      setSubscribers(data);
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const subscriber = filters.subscriber ?? null;
  const reviewRequired = filters.reviewRequired ?? null;
  useEffect(() => {
    fetchStats();
  }, [subscriber, reviewRequired]);

  const hasActiveFilters = filters.status || filters.subscriber || filters.reviewRequired !== null || 
    filters.minConfidence || filters.maxConfidence || filters.search;

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Search */}
      <div className="glass rounded-lg p-4 tech-corner">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            data-testid="search-input"
            placeholder="Search AEDs..."
            value={filters.search || ''}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-10 bg-slate-900/50 border-slate-700 text-sm"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="glass rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-cyan-400" />
            <span className="text-xs font-mono uppercase tracking-wider text-slate-400">Filters</span>
          </div>
          {hasActiveFilters && (
            <Button 
              data-testid="clear-filters-btn"
              variant="ghost" 
              size="sm" 
              onClick={clearFilters}
              className="h-6 px-2 text-xs text-slate-500 hover:text-white"
            >
              <X className="w-3 h-3 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Status Filter */}
        <div className="space-y-2">
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            Status
          </label>
          <Select 
            value={filters.status || 'all'} 
            onValueChange={(val) => updateFilter('status', val === 'all' ? null : val)}
          >
            <SelectTrigger data-testid="status-filter" className="bg-slate-900/50 border-slate-700 text-sm">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {AED_STATUS_ORDER.map((key) => {
                const value = AED_STATUSES[key];
                const config = STATUS_CONFIG[key] || STATUS_CONFIG.UNKNOWN;
                return (
                  <SelectItem key={key} value={value}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${config.dotColor}`} />
                      {config.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Subscriber Filter */}
        <div className="space-y-2">
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            Subscriber
          </label>
          <Select 
            value={filters.subscriber || 'all'} 
            onValueChange={(val) => updateFilter('subscriber', val === 'all' ? null : val)}
          >
            <SelectTrigger data-testid="subscriber-filter" className="bg-slate-900/50 border-slate-700 text-sm">
              <SelectValue placeholder="All Subscribers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Building2 className="w-3 h-3 text-slate-400" />
                  All Subscribers
                </div>
              </SelectItem>
              {subscribers.map((sub) => (
                <SelectItem key={sub} value={sub}>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-3 h-3 text-cyan-400" />
                    {sub}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Review Required Filter */}
        <div className="space-y-2">
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            Review Status
          </label>
          <Select 
            value={filters.reviewRequired === null ? 'all' : filters.reviewRequired ? 'yes' : 'no'} 
            onValueChange={(val) => updateFilter('reviewRequired', val === 'all' ? null : val === 'yes')}
          >
            <SelectTrigger data-testid="review-filter" className="bg-slate-900/50 border-slate-700 text-sm">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="yes">Needs Review</SelectItem>
              <SelectItem value="no">Reviewed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Confidence Filter */}
        <div className="space-y-3">
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            Max Confidence: {filters.maxConfidence ? `${Math.round(filters.maxConfidence * 100)}%` : '100%'}
          </label>
          <Slider
            data-testid="confidence-slider"
            value={[filters.maxConfidence ? filters.maxConfidence * 100 : 100]}
            onValueChange={([val]) => updateFilter('maxConfidence', val === 100 ? null : val / 100)}
            max={100}
            min={0}
            step={5}
            className="w-full"
          />
          <p className="text-[10px] text-slate-600">Filter AEDs with low AI confidence</p>
        </div>
      </div>

      {/* Status Summary */}
      <div className="glass rounded-lg p-4 flex-1 overflow-y-auto custom-scrollbar">
        <h3 className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3">
          Status Overview
        </h3>
        
        {loading ? (
          <div className="space-y-2">
            {AED_STATUS_ORDER.map((_, i) => (
              <div key={i} className="h-8 bg-slate-800/50 rounded animate-pulse" />
            ))}
          </div>
        ) : stats ? (
          <div className="space-y-2">
            {AED_STATUS_ORDER.map((key) => {
              const value = AED_STATUSES[key];
              const config = STATUS_CONFIG[key] || STATUS_CONFIG.UNKNOWN;
              const count = stats.status_counts?.[value] || 0;
              const percentage = stats.total_aeds > 0 
                ? ((count / stats.total_aeds) * 100).toFixed(1) 
                : 0;
              
              return (
                <button
                  key={key}
                  data-testid={`status-${key.toLowerCase()}`}
                  onClick={() => updateFilter('status', filters.status === value ? null : value)}
                  className={`w-full flex items-center justify-between p-2 rounded-lg border transition-all ${
                    filters.status === value 
                      ? `${config.bgColor} border-opacity-100` 
                      : 'border-slate-800/30 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${config.dotColor}`} />
                    <span className={`text-sm ${config.color}`}>{config.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-white">{count}</span>
                    <span className="text-[10px] text-slate-500">{percentage}%</span>
                  </div>
                </button>
              );
            })}
            
            <div className="border-t border-slate-800/50 pt-2 mt-3">
              <div className="flex items-center justify-between p-2">
                <span className="text-sm text-slate-400">Total AEDs</span>
                <span className="text-lg font-mono font-bold text-cyan-400">
                  {stats.total_aeds}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <span className="text-sm text-amber-400">Needs Review</span>
                <span className="text-lg font-mono font-bold text-amber-400">
                  {stats.review_required}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No data available</p>
        )}
      </div>

      {/* Actions */}
      <div className="glass rounded-lg p-4 space-y-2">
        <Button
          data-testid="refresh-btn"
          onClick={() => { fetchStats(); triggerRefresh(); }}
          className="w-full bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/50 text-cyan-400"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Data
        </Button>
      </div>
    </div>
  );
};
